<template>
  <div id="app">
    <!-- 浏览器 AI 助手专属视图 -->
    <WorkbenchPage v-if="isWorkbench" />
    <BrowserAgentLayout v-else-if="isBrowserAgent" :sessionId="sessionId" :forceLogin="forceLogin" />
    <!-- 移动端上传专属视图 -->
    <MobileUploadView v-else-if="isUploadMode" :uploadKey="uploadKey" />
    <!-- 原有完整聊天视图 -->
    <ChatLayout v-else />
    <!-- 雨见浏览器没开小雨语音时的顶部推荐条(侧边栏面板与 a.yjllq.com 都显示) -->
    <VoiceInviteBanner v-if="!isUploadMode" />
    <GlobalToast />
    <GlobalDialog />
    <div v-if="blockedNotice" class="account-blocked-overlay">
      <div class="account-blocked-modal">
        <div class="account-blocked-icon">⚠️</div>
        <h2>{{ blockedNotice.title || '检测到恶意使用，已封禁账号' }}</h2>
        <p>{{ blockedNotice.message || '系统检测到高危敏感词，已停止回答并限制继续使用。' }}</p>
        <p v-if="blockedNotice.blocked_until" class="account-blocked-expire">
          解封时间：{{ formatBlockedUntil(blockedNotice.blocked_until) }}
        </p>
        <div v-if="blockedNotice.matched_words && blockedNotice.matched_words.length" class="account-blocked-tags">
          <span v-for="word in blockedNotice.matched_words" :key="word">{{ word }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatLayout from './components/ChatLayout.vue';
import BrowserAgentLayout from './components/BrowserAgentLayout.vue';
import MobileUploadView from './components/MobileUploadView.vue';
import GlobalToast from './components/GlobalToast.vue';
import GlobalDialog from './components/GlobalDialog.vue';
import WorkbenchPage from './components/WorkbenchPage.vue';
import VoiceInviteBanner from './components/VoiceInviteBanner.vue';
import { SERVERS, getServerKey } from '@/api/config';
import { mutations } from '@/store';
import { syncTokenStoreModels } from '@/utils/tokenStoreSync';

export default {
  name: 'App',
  components: {
    ChatLayout,
    BrowserAgentLayout,
    MobileUploadView,
    GlobalToast,
    GlobalDialog,
    WorkbenchPage,
    VoiceInviteBanner
  },
  data() {
    return {
      isBrowserAgent: false,
      isUploadMode: false,
      sessionId: '',
      uploadKey: '',
      forceLogin: false,
      blockedNotice: null,
      isWorkbench: false
    };
  },
  async mounted() {
    const browserBridge = window.rainseeBrowser;
    if (browserBridge && typeof browserBridge.getToken === 'function') {
      window.addEventListener('rainsee-browser-account', this.handleBrowserAccount);
      try {
        const token = await browserBridge.getToken();
        if (token !== this.$store.state.token) {
          if (token) mutations.setToken(token);
          else if (this.$store.state.token) mutations.logout();
        }
      } catch (error) {
        console.warn('Could not read Rainsee browser account:', error);
      }
    }
    // 检查是否是通过移动端 Agent 唤起
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl && tokenFromUrl !== this.$store.state.token) {
      // URL token is an explicit login handoff (for example from the workbench link).
      // Persist it through the normal mutation so subsequent navigation stays logged in.
      mutations.setToken(tokenFromUrl);
    }
    // 登录回跳只通过 URL 传递一次 token；写入 store 后立即清理地址栏，避免泄露凭证。
    // 仅在指定域名下清理，其他 host（例如本地调试）保留原始 query。
    if (tokenFromUrl && window.location.hostname === 'a.yjllq.com') {
      urlParams.delete('token');
      const cleanQuery = urlParams.toString();
      const cleanUrl = `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}${window.location.hash || ''}`;
      window.history.replaceState(window.history.state, document.title, cleanUrl);
    }
    // 浏览器设置页等其它入口购买的 Token 商店模型:按账号从云端导入并切换
    syncTokenStoreModels({ force: true });
    window.addEventListener('focus', this.handleTokenStoreSync);
    document.addEventListener('visibilitychange', this.handleTokenStoreSync);
    this.isWorkbench = urlParams.get('page') === 'workbench';
    if (urlParams.get('mode') === 'agent' && urlParams.has('aisession')) {
      this.isBrowserAgent = true;
      this.sessionId = urlParams.get('aisession');
      this.forceLogin = urlParams.get('forcelogin') === 'true';
    }

    // 检查是否是移动端上传模式
    if (urlParams.get('mode') === 'upload' && urlParams.has('key')) {
      this.isUploadMode = true;
      this.uploadKey = urlParams.get('key');
    }

    console.log('App mounted, checking server speed preference...');
    this.checkServerSpeed();
    this.updateDocumentTitle();
    
    // Listen for manual trigger from config.js
    window.addEventListener('aihelp-check-server-speed', this.checkServerSpeed);
    window.addEventListener('aihelp-account-blocked', this.handleAccountBlocked);
    this.restoreAccountBlockedNotice();
  },
  destroyed() {
    window.removeEventListener('focus', this.handleTokenStoreSync);
    document.removeEventListener('visibilitychange', this.handleTokenStoreSync);
    window.removeEventListener('rainsee-browser-account', this.handleBrowserAccount);
    window.removeEventListener('aihelp-check-server-speed', this.checkServerSpeed);
    window.removeEventListener('aihelp-account-blocked', this.handleAccountBlocked);
  },
  methods: {
    handleTokenStoreSync() {
      if (document.visibilityState === 'hidden') return;
      syncTokenStoreModels();
    },
    handleBrowserAccount(event) {
      const token = event.detail || '';
      if (token && token !== this.$store.state.token) {
        mutations.setToken(token);
        syncTokenStoreModels({ force: true });
      } else if (!token && this.$store.state.token) {
        mutations.logout();
      }
    },
    restoreAccountBlockedNotice() {
      try {
        const stored = localStorage.getItem('aihelp_account_blocked');
        if (stored) {
          const blockedNotice = JSON.parse(stored);
          if (blockedNotice.blocked_until && Date.now() >= new Date(blockedNotice.blocked_until).getTime()) {
            localStorage.removeItem('aihelp_account_blocked');
            this.blockedNotice = null;
            return;
          }
          this.blockedNotice = blockedNotice;
        }
      } catch (e) {
        console.warn('Failed to restore account blocked state:', e);
      }
    },
    handleAccountBlocked(event) {
      const payload = event.detail || {};
      this.blockedNotice = {
        ...payload,
        blocked_until: payload.blocked_until || new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        title: '检测到恶意使用，已封禁账号',
        message: '系统检测到高危敏感词，已停止回答并限制继续使用 2 小时。',
        ...(payload.title ? { title: payload.title } : {}),
        ...(payload.message ? { message: payload.message } : {})
      };
    },
    formatBlockedUntil(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    updateDocumentTitle() {
      if (this.isWorkbench) {
        document.title = '个人工作台';
        return;
      }
      const hostname = window.location.hostname;
      let title = '';

      if (hostname.includes('yjllq.com')) {
        title = this.$t('app.rainseeTitle');
      } else if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname === 'localhost') {
        title = this.$t('app.aihelpTitle');
      } else {
        // Extract brand name: e.g. www.sioyie.com -> SIOYIE AI
        const brandPart = hostname.replace(/^www\./, '').split('.')[1];
        title = brandPart.toUpperCase() + ' AI';
      }

      if (title) {
        document.title = title;
      }
    },
    async checkServerSpeed() {
      // 1. Determine Target URL (Active Model Endpoint)
      let targetUrl = 'https://api.rainsee.cn/v1/chat/completions'; // Default fallback
      try {
        const settings = this.$store.state.settings;

        if (settings && settings.models) {
          const activeModelId = settings.activeTextModelId || settings.activeModelId;
          const activeModel = settings.models.find(m => m.id === activeModelId);
          if (activeModel && activeModel.endpoint) {
            targetUrl = activeModel.endpoint;
          }
        }
      } catch (e) {
        console.warn('Could not retrieve active model endpoint, using default.', e);
      }
      
      console.log('Speed test target URL:', targetUrl);

      // 2. Generate Key based on Target URL
      const storageKey = getServerKey(targetUrl);
      const stored = localStorage.getItem(storageKey);

      // 3. Check if we need to run test
      if (!stored) {
        console.log(`No preference found for ${targetUrl} (key: ${storageKey}). Testing server speeds...`);
        
        const tests = SERVERS.map(async (server) => {
          try {
            // Test the server's connection speed to the target (base64 encoded as requested)
            const response = await fetch(`https://${server}/AiAssistantv5.php?action=speed_test&url=${encodeURIComponent(btoa(targetUrl))}`);
            const data = await response.json();
            
            if (data.status === 'success') {
               // Prefer server's reported connection time (total_time is in seconds)
               return { server, time: data.total_time };
            }
            return { server, time: 9999 };
          } catch (e) {
            console.error(`Speed test failed for ${server}`, e);
            return { server, time: 9999 };
          }
        });

        const results = await Promise.all(tests);
        // Sort by time ascending
        results.sort((a, b) => a.time - b.time);
        
        const winner = results[0].server;
        console.log('Selected fastest server:', winner, results);
        localStorage.setItem(storageKey, winner);
      } else {
          console.log(`Using cached server for ${targetUrl}:`, stored);
      }
    }
  }
}
</script>

<style>
/* Global Reset & Typography */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.markdown-body pre>code{
      white-space: break-spaces !important;
}

.markdown-body pre{
  margin-bottom:0px !important;
  padding:10px !important;
      background-color: transparent;
    overflow-x:auto !important;
}

.markdown-body table{
      width: 100%;
    overflow-x: auto;
}

.markdown-body {
  font-size:unset !important
}

/* Markdown Table Styling */
.markdown-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.markdown-body table th,
.markdown-body table td {
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  text-align: left;
}

.markdown-body table th {
  background-color: rgba(0, 0, 0, 0.03);
  font-weight: 600;
}

.markdown-body table tr:nth-child(2n) {
  background-color: rgba(0, 0, 0, 0.01);
}

[data-theme="dark"] .markdown-body table {
  border-color: rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .markdown-body table th,
[data-theme="dark"] .markdown-body table td {
  border-color: rgba(255, 255, 255, 0.08);
}

[data-theme="dark"] .markdown-body table tr {
  background-color: transparent !important; /* Fix white background in dark mode */
}

[data-theme="dark"] .markdown-body table th {
  background-color: rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .markdown-body table tr:nth-child(2n) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--bg-main);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* CSS Variables System */
:root {
  /* Semantic Font Sizes - No PX allowed in components */
  --font-small: 0.875rem;   /* 14px */
  --font-medium: 1rem;      /* 16px */
  --font-large: 1.25rem;    /* 20px */
  --font-xl: 1.5rem;        /* 24px */

  /* Light Theme (iOS Style - Clean & Airy) */
  --bg-main: #ffffff; /* iOS System Gray 6 */
  --bg-surface: rgba(255, 255, 255, 0.75); /* Blur base */
  --bg-input: rgba(255, 255, 255, 0.8);
  --bg-message-user: #007aff; /* iOS Blue */
  --bg-message-assistant: #ffffff;
  
  --primary-color: #007aff;
  --primary-gradient: linear-gradient(135deg, #007aff 0%, #00c6ff 100%);
  --accent-color: #5856d6;
  --accent-hover: #4a48b8;
  
  --text-primary: #000000;
  --text-secondary: #8e8e93; /* iOS Gray */
  --text-on-primary: #ffffff;
  
  --border-color: transparent; /* Removed lines as requested */
  --shadow-soft: 0 8px 40px rgba(0, 0, 0, 0.08);
  --shadow-card: 0 12px 48px -12px rgba(0, 0, 0, 0.12);
  --radius-lg: 24px;
  --radius-md: 18px;
  --radius-sm: 12px;
}

[data-theme="dark"] {
  /* Night Mode (iOS Dark) */
  --bg-main: #000000;
  --bg-surface: rgba(28, 28, 30, 0.75); /* iOS System Gray 6 Dark */
  --bg-input: rgba(44, 44, 46, 0.8);
  --bg-message-user: #0a84ff;
  --bg-message-assistant: #1c1c1e;

  --primary-color: #0a84ff;
  --primary-gradient: linear-gradient(135deg, #0a84ff 0%, #00d4ff 100%);
  --accent-color: #5e5ce6;
  --accent-hover: #7d7aff;
  
  --text-primary: #ffffff;
  --text-secondary: #98989d;
  --text-on-primary: #ffffff;
  
  --border-color: transparent;
  --shadow-soft: 0 8px 40px rgba(0, 0, 0, 0.4);
  --shadow-card: 0 12px 48px -12px rgba(0, 0, 0, 0.6);
}



/* Utilities */
.text-small { font-size: var(--font-small); }
.text-medium { font-size: var(--font-medium); }
.text-large { font-size: var(--font-large); }

button {
  font-family: inherit;
}

textarea {
  font-family: inherit;
}

.account-blocked-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(8, 12, 24, 0.72);
  backdrop-filter: blur(12px);
}

.account-blocked-modal {
  width: min(420px, 100%);
  padding: 32px 28px;
  text-align: center;
  background: var(--bg-message-assistant, #fff);
  color: var(--text-primary, #111827);
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
}

.account-blocked-icon {
  margin-bottom: 12px;
  font-size: 48px;
}

.account-blocked-modal h2 {
  margin: 0 0 12px;
  font-size: 22px;
  line-height: 1.35;
}

.account-blocked-modal p {
  margin: 0;
  color: var(--text-secondary, #4b5563);
  font-size: 15px;
  line-height: 1.7;
}

.account-blocked-expire {
  margin-top: 10px !important;
  color: var(--text-primary, #111827) !important;
  font-weight: 600;
}

.account-blocked-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 18px;
}

.account-blocked-tags span {
  padding: 4px 10px;
  color: #991b1b;
  background: #fee2e2;
  border-radius: 999px;
  font-size: 12px;
}
</style>
